import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Invoice } from '@nxsys/types/models/invoice';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { toNumber } from 'lodash';
import moment, { Moment } from 'moment';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

type ModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (datePaid: string, receipt: number, reference: string) => void;
  isMarkAsPaidPending: boolean;
  data: Invoice[];
};

type FormValues = {
  datePaid: string;
  amountPaid: number;
  reference?: string;
};

const MarkAsPaidModal: React.FC<ModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  isMarkAsPaidPending,
  data,
}: ModalProps) => {
  const { messages } = useTypedIntl();

  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const [form] = Form.useForm<FormValues>();

  const onFinish: FormProps['onFinish'] = (values: FormValues) => {
    onSubmit(
      moment(values.datePaid).toISOString(),
      values.amountPaid,
      values.reference ?? '',
    );
  };

  const onFinishFailed: FormProps['onFinishFailed'] = () => {
    // noop - form validation handles error display
  };

  const maxPayable = useMemo(() => {
    if (!data?.length) return 0;
    return data.reduce((sum: number, inv: Invoice) => {
      return sum + toNumber(inv?.outstanding || 0);
    }, 0);
  }, [data]);

  return (
    <AppModalContainer
      title={messages['common.addPayment']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.add'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isMarkAsPaidPending,
          disabled: isMarkAsPaidPending,
        },
      ]}
      minHeight="300px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          datePaid: moment().toISOString(),
        }}
      >
        <Form.Item
          key={'datePaid'}
          label={messages['common.datePaid']}
          name={'datePaid'}
          rules={[
            { required: true, message: messages['common.pleaseSelectDate'] },
          ]}
        >
          <AppDatePicker
            placeholder={messages['common.selectDate']}
            onChange={(date: Moment) => {
              form.setFieldsValue({
                datePaid: date.toISOString(),
              });
            }}
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item
          key={'amountPaid'}
          label={messages['common.amountPaid']}
          name={'amountPaid'}
          rules={[
            { required: true, message: messages['common.pleaseEnterAmount'] },
            {
              validator: (_, value) => {
                if (toNumber(value) > maxPayable) {
                  return Promise.reject(
                    new Error(messages['common.amountCannotExceedOutstanding']),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <AppCurrencyInput
            prefix={showCurrencyByCountry(payrollCountry)}
            placeholder={messages['common.enterAmount']}
          />
        </Form.Item>

        <Form.Item
          key={'reference'}
          label={messages['common.reference']}
          name={'reference'}
        >
          <AppInput placeholder={messages['common.enterReference']} />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default MarkAsPaidModal;
